/* Cart orchestration for the shared embroidery editor. No product-page navigation. */
(() => {
  const storageKey = 'loulou-cart-embroidery-update';
  const dismissedKey = 'loulou-cart-embroidery-dismissed';
  const root = () => window.Shopify.routes.root;
  const properties = item => item.properties || {};
  const stable = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
  const planId = item => item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null;
  const sameLine = (a, b) => Number(a.variant_id || a.id) === Number(b.variant_id || b.id) &&
    stable(properties(a)) === stable(properties(b)) && planId(a) === planId(b);
  const findLine = (cart, item) => cart.items.find(line => sameLine(line, item));
  const request = async (path, body) => {
    const response = await fetch(`${root()}${path}`, body === undefined ? { cache: 'no-store' } : {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || 'Unable to update your cart. Please try again.');
    return data;
  };

  // Persist before the first mutation. An interrupted page can reconcile without
  // repeating an add whose response was lost. Recovery restores only this group.
  class CartEmbroideryUpdate {
    constructor(storage = sessionStorage) { this.storage = storage; }
    pending() { return JSON.parse(this.storage.getItem(storageKey) || 'null'); }
    record(update) { this.storage.setItem(storageKey, JSON.stringify(update)); }
    clear() { this.storage.removeItem(storageKey); }
    async save(source, quantity, details, feeId) {
      if (this.pending()) throw new Error('Please review the previous cart update first.');
      const cart = await request('cart.js');
      const parent = cart.items.find(item => item.key === source.key);
      if (!parent || parent.quantity !== source.quantity || parent.variant_id !== source.variantId) {
        throw new Error('Your cart changed. Go back and choose the item again.');
      }
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > parent.quantity ||
        quantity % source.step || quantity < source.min ||
        (parent.quantity !== quantity && (parent.quantity - quantity < source.min || (parent.quantity - quantity) % source.step))) {
        throw new Error('Choose a quantity allowed for this item.');
      }
      if (properties(parent)._product_match || properties(parent)['Embroidery Text'] || parent.parent_relationship) {
        throw new Error('This item has already changed. Please choose it again.');
      }
      const children = cart.items.filter(item => item.parent_relationship?.parent_key === parent.key);
      if (children.some(item => item.quantity !== parent.quantity)) {
        throw new Error('The attached add-on quantities differ. Please review this item in your cart before customizing.');
      }
      const token = crypto.randomUUID();
      const group = children.length || properties(parent)._add_on_group ? crypto.randomUUID() : null;
      const parentProperties = { ...properties(parent), ...details, _product_match: token };
      if (group) parentProperties._add_on_group = group;
      const items = [{ id: parent.variant_id, quantity, properties: parentProperties }, {
        id: feeId, quantity, properties: { For: source.title, _variant_id: String(parent.variant_id), _product_match: token }
      }, ...children.map(item => ({
        id: item.variant_id, quantity, parent_id: parent.variant_id,
        properties: { ...properties(item), _add_on_group: group }
      }))];
      if (planId(parent)) items[0].selling_plan = planId(parent);
      children.forEach((item, i) => { if (planId(item)) items[i + 2].selling_plan = planId(item); });
      const update = { token, quantity, originals: [parent, ...children], items, stage: 'reduce' };
      this.record(update);
      try {
        // Release inventory before re-adding the customized copies. Shopify
        // removes nested children with a removed parent; explicit quantities
        // also preserve the retained plain copies when splitting a line.
        await request('cart/update.js', { updates: Object.fromEntries(update.originals.map(item => [item.key, item.quantity - quantity])) });
        update.stage = 'add';
        this.record(update);
        await request('cart/add.js', { items });
      } catch (error) {
        const result = await this.recover();
        if (result === 'saved') return result;
        throw new Error(`${error.message} Your original items have been restored.`);
      }
      return this.recover();
    }
    async recover() {
      const update = this.pending();
      if (!update) return 'unchanged';
      let cart = await request('cart.js');
      const originalCount = original => findLine(cart, original)?.quantity || 0;
      const reduced = update.originals.every(item => originalCount(item) === item.quantity - update.quantity);
      const restored = update.originals.every(item => originalCount(item) === item.quantity);
      const added = update.items.map(item => findLine(cart, item));
      const complete = added.every(item => item?.quantity === update.quantity) &&
        added.slice(2).every(item => item.parent_relationship?.parent_key === added[0].key);
      if (reduced && complete && update.stage !== 'restore') {
        this.clear();
        return 'saved';
      }
      if (restored && added.every(item => !item)) {
        this.clear();
        return 'restored';
      }
      // Stop if another tab changed source quantities. Never overwrite its work.
      if (!reduced && !restored) throw new Error('Your cart changed during the update. Please review the cart before retrying.');
      if (added.some(item => item && item.quantity !== update.quantity)) throw new Error('Please review the changed embroidery quantities in your cart.');
      update.stage = 'restore';
      this.record(update);
      if (added.some(Boolean)) {
        await request('cart/update.js', { updates: Object.fromEntries(added.filter(Boolean).map(item => [item.key, 0])) });
        cart = await request('cart.js');
      }
      if (reduced) {
        const originalParent = update.originals[0];
        const retainedParent = findLine(cart, originalParent);
        // Restore the released quantity, using the retained parent's exact key
        // for nested items, or parent_id when restoring the entire group.
        const items = update.originals.map((item, index) => {
          const restoredItem = { id: item.variant_id, quantity: update.quantity, properties: properties(item) };
          if (planId(item)) restoredItem.selling_plan = planId(item);
          if (index) {
            if (retainedParent) restoredItem.parent_line_key = retainedParent.key;
            else restoredItem.parent_id = originalParent.variant_id;
          }
          return restoredItem;
        });
        await request('cart/add.js', { items });
      }
      cart = await request('cart.js');
      if (!update.originals.every(item => (findLine(cart, item)?.quantity || 0) === item.quantity) ||
        update.items.some(item => findLine(cart, item))) {
        throw new Error('The cart update needs another review. Please select Review cart update.');
      }
      this.clear();
      return 'restored';
    }
  }

  class CartEmbroidery {
    constructor(dialog) {
      this.dialog = dialog;
      this.transaction = new CartEmbroideryUpdate();
      this.dismissed = new Set(this.readDismissed());
      this.overview = dialog.querySelector('[data-cart-embroidery-overview]');
      this.itemState = dialog.querySelector('[data-cart-embroidery-item-state]');
      this.editor = dialog.querySelector('[data-cart-embroidery-editor]');
      this.status = dialog.querySelector('[data-cart-embroidery-status]');
      this.recovery = dialog.querySelector('[data-cart-embroidery-recover]');
      document.addEventListener('click', event => {
        const button = event.target.closest('[data-cart-embroidery-open]');
        if (!button) return;
        event.preventDefault();
        const source = JSON.parse(button.closest('[data-cart-embroidery-item]').querySelector('[data-cart-embroidery-data]').textContent);
        if (this.dialog.contains(button)) {
          if (!this.busy) this.edit(source).catch(error => this.error(error));
        } else this.open(source, button).catch(error => this.error(error));
      });
      document.addEventListener('submit', event => this.checkout(event), true);
      dialog.querySelectorAll('[data-cart-embroidery-close]').forEach(button => button.addEventListener('click', () => this.close()));
      dialog.querySelector('[data-cart-embroidery-continue]').addEventListener('click', () => this.continue());
      this.recovery.addEventListener('click', () => this.recover());
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      dialog.addEventListener('keydown', event => {
        if (event.key === 'Tab') event.stopPropagation();
      });
      if (this.transaction.pending()) this.open(null, null, 'item').then(() => this.recover()).catch(error => this.error(error));
    }
    readDismissed() {
      try { return JSON.parse(sessionStorage.getItem(dismissedKey) || '[]'); } catch (_) { return []; }
    }
    candidates() {
      return [...new Map(Array.from(document.querySelectorAll('[data-cart-embroidery-data]')).filter(node => !this.dialog.contains(node)).map(node => {
        const item = JSON.parse(node.textContent);
        return [item.key, item];
      })).values()];
    }
    async checkout(event) {
      if (event.submitter?.name !== 'checkout' || event.submitter.disabled ||
        !event.target.matches('.cart-page, .cart-drawer__footer')) return;
      if (this.bypass && !this.transaction.pending() && !this.needsRefresh) { this.bypass = false; return; }
      if (!this.transaction.pending() && !this.needsRefresh && !this.candidates().some(item => !this.dismissed.has(item.reminderId || item.key))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkoutForm = event.target;
      this.open().catch(error => this.error(error));
    }
    async open(source, trigger, flow = source ? 'item' : 'checkout') {
      if (this.busy) return;
      if (!this.dialog.open) {
        this.flow = flow;
        this.saved = false;
        if (flow === 'item') this.checkoutForm = null;
        this.productPages = new Map();
        this.returnFocus = trigger || this.checkoutForm?.querySelector('[name="checkout"]') || document.activeElement;
        this.returnSurface = this.returnFocus?.closest('cart-drawer, .shopify-section--main-cart');
        this.previousOverflow = document.body.style.overflow;
        this.unmount();
        this.status.textContent = '';
        this.recovery.hidden = true;
        this.dialog.setAttribute('aria-labelledby', flow === 'item' ? 'cart-embroidery-item-title' : 'cart-embroidery-title');
        if (flow === 'checkout') this.show();
      }
      if (this.transaction.pending() || this.needsRefresh) {
        this.status.textContent = 'A previous cart update needs to be reviewed before continuing.';
        this.recovery.hidden = false;
        this.show();
        return;
      }
      if (source) {
        trigger?.setAttribute('aria-busy', 'true');
        try { await this.edit(source); }
        finally { trigger?.removeAttribute('aria-busy'); }
      }
      else if (this.flow === 'checkout') {
        this.busy = true;
        try { await this.list(); } finally { this.busy = false; }
      }
    }
    show() {
      if (this.dialog.open) return;
      // The native modal owns focus while it is open. Suspend the drawer's
      // existing trap so its outside-click handler does not close the drawer.
      const drawer = this.returnSurface?.matches('cart-drawer') ? this.returnSurface : null;
      const trap = drawer?.open ? drawer.focusTrap : null;
      if (trap?.active && !trap.paused) {
        this.pausedTrap = trap;
        trap.pause();
      }
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
    async list(message = '') {
      this.unmount();
      this.status.textContent = message || 'Loading embroidery options...';
      const response = await fetch(`${root()}cart?section_id=cart-embroidery-items`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to load embroidery options. Please try again.');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = parsed.querySelector('[data-cart-embroidery-list]');
      if (!list) throw new Error('Unable to load embroidery options. Please try again.');
      this.dialog.querySelector('[data-cart-embroidery-list-host]').replaceChildren(list);
      this.status.textContent = message;
      const hasAvailableItems = !!list.querySelector('[data-cart-embroidery-open]');
      const hasEmbroideredItems = !!list.querySelector('[data-cart-embroidery-completed]');
      if (!hasAvailableItems && hasEmbroideredItems && message.startsWith('Embroidery saved.')) {
        this.status.textContent = 'Embroidery saved. Review your items or continue to checkout.';
      }
      this.dialog.querySelector('[data-cart-embroidery-description]').textContent = hasAvailableItems
        ? 'These items can be embroidered. Choose an item to see its options and additional price.'
        : hasEmbroideredItems ? 'Your embroidery details are saved. Review your items before continuing to checkout.'
          : 'No items in your cart currently need embroidery.';
      this.dialog.querySelector('[data-cart-embroidery-continue]').textContent = 'Continue to checkout';
      this.loadPrices(list);
      this.dialog.setAttribute('aria-labelledby', 'cart-embroidery-title');
      this.dialog.querySelector('#cart-embroidery-title').focus();
    }
    async assets() {
      if (window.loulouEmbroidery) return;
      if (!this.assetPromise) this.assetPromise = new Promise((resolve, reject) => {
        const style = document.createElement('link');
        style.rel = 'stylesheet'; style.href = this.dialog.dataset.embroideryStyle;
        document.head.append(style);
        const script = document.createElement('script');
        script.src = this.dialog.dataset.embroideryScript;
        script.onload = resolve;
        script.onerror = () => { this.assetPromise = null; script.remove(); reject(new Error('Unable to load the embroidery editor. Please try again.')); };
        document.head.append(script);
      });
      await this.assetPromise;
    }
    async productPage(source) {
      const url = new URL(source.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Unable to load this item.');
      url.searchParams.set('variant', source.variantId);
      this.productPages ||= new Map();
      if (!this.productPages.has(url.href)) {
        const page = fetch(url, { cache: 'no-store' }).then(async response => {
          if (!response.ok) throw new Error('Unable to load this item. Please try again.');
          return new DOMParser().parseFromString(await response.text(), 'text/html');
        }).catch(error => { this.productPages.delete(url.href); throw error; });
        this.productPages.set(url.href, page);
      }
      return this.productPages.get(url.href);
    }
    configuration(parsed, source) {
      const modal = parsed.querySelector('.embroidery-modal');
      const form = modal?.querySelector('#embroidery-form');
      const feeId = Number(form?.dataset.embroideryVariant);
      const productForm = Array.from(parsed.querySelectorAll('.js-product-form')).find(node =>
        Number(node.querySelector('[name="id"]')?.value) === source.variantId);
      const feeReference = parsed.querySelector('.js-embroidery-product-id')?.value;
      if (!form || form.dataset.isFrame === 'true' || !feeId || !productForm ||
        (feeReference && Number(feeReference) !== feeId)) throw new Error('Embroidery is unavailable for this item right now. You can continue without embroidery.');
      return { modal, form, feeId };
    }
    loadPrices(list) {
      list.querySelectorAll('[data-cart-embroidery-data]').forEach(async node => {
        const source = JSON.parse(node.textContent);
        const button = node.closest('[data-cart-embroidery-item]').querySelector('[data-cart-embroidery-open]');
        button.disabled = true;
        button.textContent = 'Loading embroidery...';
        try {
          const { form } = this.configuration(await this.productPage(source), source);
          const price = form.dataset.embroideryPrice;
          if (!price) throw new Error('Embroidery price is unavailable.');
          button.textContent = `Add Embroidery (${price})`;
          button.disabled = false;
        } catch (error) {
          button.textContent = 'Embroidery unavailable';
          button.title = error.message;
        }
      });
    }
    async edit(source) {
      this.busy = true;
      this.status.textContent = 'Loading this item...';
      try {
        const [page] = await Promise.all([this.productPage(source), this.assets()]);
        const parsed = page.cloneNode(true);
        const { modal, form, feeId } = this.configuration(parsed, source);
        // Retain only the shared form and its JSON configuration. Do not execute
        // product-page scripts or mount another product form/variant controller.
        modal.querySelectorAll('script:not([type="application/json"])').forEach(node => node.remove());
        modal.querySelectorAll('[id]').forEach(node => {
          if (['embroidery-form', 'embroidery-compatibility-data'].includes(node.id)) return;
          const oldId = node.id;
          node.id = `cart-${oldId}`;
          modal.querySelectorAll('label').forEach(label => { if (label.htmlFor === oldId) label.htmlFor = node.id; });
        });
        const context = document.createElement('div');
        const selected = document.createElement('div');
        selected.className = 'variant-picker'; selected.hidden = true;
        parsed.querySelectorAll('variant-picker:not(product-add-ons variant-picker) [data-option-name], .variant-picker [data-option-name]').forEach(fieldset => {
          if (fieldset.closest('product-add-ons')) return;
          const field = document.createElement('fieldset'); field.dataset.optionName = fieldset.dataset.optionName;
          fieldset.querySelectorAll('input:checked').forEach(input => {
            const copy = input.cloneNode(true);
            // Compatibility reads only the selected value. Keep these copies
            // out of the live PDP's radio groups, labels, and submitted data.
            ['form', 'name', 'id'].forEach(attribute => copy.removeAttribute(attribute));
            field.append(copy);
          });
          selected.append(field);
        });
        context.append(selected, modal);
        this.editor.replaceChildren(context);
        modal.classList.add('open');
        modal.querySelector('.embroidery-modal__overlay')?.remove();
        const heading = modal.querySelector('.embroidery-modal__title');
        heading.textContent = source.title; heading.id = 'cart-embroidery-editor-title'; heading.tabIndex = -1;
        this.dialog.setAttribute('aria-labelledby', heading.id);
        const controls = document.createElement('div'); controls.className = 'embroidery-form__input-group';
        const multipleCopies = source.quantity > 1;
        const label = document.createElement('label'); label.htmlFor = 'cart-embroidery-quantity'; label.textContent = `How many to embroider? (${source.quantity} in cart)`;
        const quantity = document.createElement('input'); quantity.type = multipleCopies ? 'number' : 'hidden'; quantity.id = label.htmlFor;
        quantity.min = source.min; quantity.max = source.quantity; quantity.step = source.step; quantity.value = multipleCopies ? source.min : 1;
        if (multipleCopies) controls.append(label);
        controls.append(quantity);
        const hint = document.createElement('p'); hint.className = 'text-sm';
        hint.textContent = `Embroidery: ${form.dataset.embroideryPrice} per item. ${multipleCopies ? 'Choose one for a different name on each copy, or several for the same details. ' : ''}Attached add-ons stay with their items.`;
        controls.append(hint);
        form.querySelector('.embroidery-form__scroll').prepend(controls);
        form.querySelector('.js-embroidery-submit').textContent = 'Save embroidery';
        form.addEventListener('submit', event => { event.preventDefault(); window.addEmbroidery(); });
        window.loulouEmbroidery.mount(context, () => this.save(source, quantity, feeId), () => this.back());
        this.overview.hidden = true; this.itemState.hidden = true; this.editor.hidden = false; this.status.textContent = '';
        this.show();
        heading.focus();
      } finally { this.busy = false; }
    }
    async save(source, quantityInput, feeId) {
      if (this.busy) return;
      if (!quantityInput.reportValidity()) return;
      const value = selector => this.editor.querySelector(selector)?.value;
      const details = {
        'Embroidery Text': value('.js-embroidery-text').trim(),
        'Thread Color': value('.js-embroidery-color-option.selected input'),
        Font: value('.js-embroidery-font-option.selected input:not(:disabled)'),
        Placement: value('.js-embroidery-placement-option.selected input')
      };
      if (Object.values(details).some(value => !value)) return;
      this.busy = true;
      this.dialog.setAttribute('aria-busy', 'true');
      const submit = this.editor.querySelector('.js-embroidery-submit');
      submit.disabled = true;
      this.status.textContent = 'Saving embroidery...';
      this.needsRefresh = true;
      try {
        const result = await this.transaction.save(source, Number(quantityInput.value), details, feeId);
        if (result !== 'saved') throw new Error('The embroidery could not be saved. Your original items have been restored.');
        this.saved = true;
        await this.refreshCart();
        this.needsRefresh = false;
        await this.finish('Embroidery saved. Choose another item or continue to checkout.');
      } catch (error) {
        if (!this.transaction.pending()) {
          try { await this.refreshCart(); this.needsRefresh = false; } catch (_) { /* Review retries rendering, never the mutation. */ }
        }
        this.error(error);
      }
      finally {
        this.busy = false; this.dialog.removeAttribute('aria-busy');
        if (!this.transaction.pending()) submit.disabled = false;
      }
    }
    async refreshCart() {
      const sections = window.loulouCart.prepareSections();
      const main = document.querySelector('.shopify-section--main-cart');
      const mainId = main?.id.replace('shopify-section-', '');
      if (mainId) sections.push(mainId);
      const cart = await window.loulouCart.getCart({}, [...new Set(sections)]);
      if (mainId && cart.sections[mainId]) {
        const html = new DOMParser().parseFromString(cart.sections[mainId], 'text/html');
        const replacement = html.getElementById(main.id);
        if (!replacement) throw new Error('Embroidery saved. Please refresh the cart to see the updated items.');
        const note = main.querySelector('[name="note"]')?.value;
        if (note !== undefined && replacement.querySelector('[name="note"]')) replacement.querySelector('[name="note"]').value = note;
        if (this.returnSurface === main) this.returnSurface = replacement;
        main.replaceWith(replacement);
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'embroidery:save', cart } }));
    }
    async recover() {
      if (this.busy) return;
      this.busy = true;
      this.status.textContent = 'Reviewing the cart update...';
      try {
        const result = await this.transaction.recover();
        this.saved ||= result === 'saved';
        this.recovery.hidden = true;
        this.needsRefresh = true;
        await this.refreshCart();
        this.needsRefresh = false;
        await this.finish(result === 'saved' ? 'Embroidery saved.' : result === 'restored' ? 'Your original items have been restored.' : 'Cart refreshed.');
      } catch (error) { this.error(error); }
      finally { this.busy = false; }
    }
    error(error) {
      this.status.textContent = error.message || 'Unable to update the cart. Please try again.';
      this.recovery.hidden = !this.transaction.pending() && !this.needsRefresh;
      if (this.transaction.pending() || this.needsRefresh) this.unmount();
      this.show();
      this.status.focus();
    }
    unmount() {
      window.loulouEmbroidery?.unmount();
      this.editor.replaceChildren(); this.editor.hidden = true;
      this.overview.hidden = this.flow !== 'checkout';
      this.itemState.hidden = this.flow === 'checkout';
      if (this.flow !== 'checkout') this.dialog.setAttribute('aria-labelledby', 'cart-embroidery-item-title');
    }
    async finish(message) {
      if (this.flow === 'item') {
        this.busy = false;
        this.close();
      } else await this.list(message);
    }
    async back() {
      if (this.busy || this.transaction.pending()) return;
      if (this.flow === 'item') return this.close();
      try { await this.list(); } catch (error) { this.error(error); }
    }
    dismiss() {
      this.candidates().forEach(item => this.dismissed.add(item.reminderId || item.key));
      try { sessionStorage.setItem(dismissedKey, JSON.stringify([...this.dismissed])); } catch (_) { /* In-memory dismissal still works. */ }
    }
    close() {
      if (this.busy) return;
      if (this.flow === 'checkout' && !this.transaction.pending()) this.dismiss();
      this.unmount(); this.dialog.close();
      document.body.style.overflow = this.previousOverflow;
      this.pausedTrap?.unpause();
      this.pausedTrap = null;
      if (this.returnSurface?.matches('cart-drawer') && !this.returnSurface.hasAttribute('open')) this.returnSurface.show();
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
      else (this.returnSurface?.querySelector('[data-cart-embroidery-open], [name="checkout"]') ||
        document.querySelector('.cart-page [name="checkout"], cart-drawer [name="checkout"]'))?.focus();
    }
    continue() {
      if (this.busy || this.transaction.pending() || this.needsRefresh) return;
      const form = this.checkoutForm?.isConnected ? this.checkoutForm : this.checkoutForm ?
        document.querySelector(this.checkoutForm.matches('.cart-page') ? '.cart-page' : '.cart-drawer__footer') :
        document.querySelector('.cart-page') || document.querySelector('.cart-drawer__footer');
      const button = form?.querySelector('[name="checkout"]');
      this.close();
      if (button && !button.disabled) { this.bypass = true; form.requestSubmit(button); }
    }
  }
  // Expose the transaction for the existing local browser fixture approach.
  window.loulouCartEmbroidery = { CartEmbroideryUpdate };
  const dialog = document.getElementById('cart-embroidery-dialog');
  if (dialog && typeof dialog.showModal === 'function') new CartEmbroidery(dialog);
})();
